import type { ComponentType, JSX } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { ArrowRightIcon } from '@radix-ui/react-icons';

export interface DashboardStatsWidgetProps {
  label: string;
  value: string;
  to: string;
  icon?: ComponentType<{ className?: string }>;
  detail?: string;
  className?: string;
}

export function DashboardStatsWidget({
  label,
  value,
  to,
  icon: Icon,
  detail,
  className,
}: DashboardStatsWidgetProps): JSX.Element {
  return (
    <Link
      to={to}
      className={clsx(
        'group flex h-full flex-col gap-3 rounded-2xl border border-surface-subtle bg-white/90 px-5 py-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-brand-primary/40 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2',
        className,
      )}
      aria-label={`${label}: ${value}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          {Icon ? <Icon aria-hidden="true" className="h-5 w-5 text-brand-primary" /> : null}
          <p className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
            {label}
          </p>
        </div>
        <ArrowRightIcon
          aria-hidden="true"
          className="mt-0.5 h-4 w-4 text-text-primary/40 transition group-hover:translate-x-1 group-hover:text-brand-primary"
        />
      </div>

      <p className="text-3xl font-semibold text-text-primary">{value}</p>

      {typeof detail === 'string' && detail.trim().length > 0 ? (
        <p className="text-sm text-text-primary/70">{detail}</p>
      ) : (
        <span className="mt-auto" aria-hidden="true" />
      )}
    </Link>
  );
}
